import { Card, Row, Col, Badge } from 'react-bootstrap';
import { useDrop } from 'react-dnd';
import { PIPELINE_STATUS_OPTIONS } from 'constants/globalConstants';
import { $filter, $view } from 'signals/global.signals';
import { handleClickStageHeader, handleDropItem } from 'components/global/_helpers/board.events';
import FounderPipelineGridItem from 'components/views/FounderPipeline/_components/FounderPipelineGridItem';
import FounderPipelineListItem from 'components/views/FounderPipeline/_components/FounderPipelineListItem';
import { updateInvestorOnFounder } from 'api/founderPipeline.api';

const DropZone = ({ children, stageKey }) => {
  // Access the signal value to create a subscription
  const { isDragDisabled } = $view.value;

  const [{ isOver, canDrop }, drop] = useDrop(() => ({
    accept: 'investor',
    canDrop: () => !isDragDisabled,
    drop: async (item) => {
      if (item.pipelineItem.pipelineStatus !== stageKey) {
        await handleDropItem({
          droppedItem: item.pipelineItem,
          droppedStageValue: stageKey,
          updateCallback: updateInvestorOnFounder,
        });
      }
    },
    collect: (monitor) => ({
      isOver: monitor.isOver(),
      canDrop: monitor.canDrop(),
    }),
  }), [stageKey, isDragDisabled]);

  return (
    <div
      ref={isDragDisabled ? null : drop}
      style={{
        backgroundColor: isOver && canDrop ? 'rgba(0, 255, 0, 0.1)' : 'transparent',
        border: isOver && canDrop ? '2px dashed #28a745' : 'none',
        borderRadius: '4px',
        minHeight: '100px',
        transition: 'all 0.2s ease',
      }}
      className={isDragDisabled ? 'opacity-50' : 'cursor-move'}
    >
      {children}
    </div>
  );
};

const FounderPipelineCards = ({ pipelineData }) => PIPELINE_STATUS_OPTIONS
  .filter((stage) => !$filter.value.pipelineStatuses?.length || $filter.value.pipelineStatuses.includes(stage.value))
  .map((stage) => {
    const pipelineItems = pipelineData
      ?.filter((investor) => {
        const isInStage = stage.value.toLowerCase() === investor.pipelineStatus?.toLowerCase();
        const isStatusMatch = $filter.value.pipelineStatuses?.length
          ? $filter.value.pipelineStatuses?.includes(investor.pipelineStatus)
          : true;
        const isDealMatch = $filter.value?.dealIds?.length > 0
          ? $filter.value.dealIds?.includes(investor.dealId)
          : true;
        const isSearchMatch = $filter.value.pipelineSearch
          ? investor.organization?.name?.toLowerCase().includes($filter.value.pipelineSearch?.toLowerCase())
          : true;

        return isInStage && isStatusMatch && isDealMatch && isSearchMatch;
      }) || [];

    return (
      <Card
        className={
          $view.value.view === 'grid'
            ? 'bg-transparent border-0 flex-shrink-0'
            : 'bg-transparent border-0 mb-16 w-100'
        }
        key={stage.id}
        style={
          $view.value.view === 'grid'
            ? {
              minWidth: 350,
              maxWidth: 400,
              width: 100,
              display: 'inline-block',
              verticalAlign: 'top',
            }
            : {
              width: '100%',
            }
        }
      >
        <Card.Header onClick={() => handleClickStageHeader(stage.id)}>
          <Row>
            <Col>
              <h6 className={`fw-600 mb-0 text-gradient-${stage.variant}-white`}>{stage.label}</h6>
            </Col>
            <Col xs="auto">
              <Badge bg="transparent" className="text-white">{pipelineItems?.length} in {stage.label}</Badge>
            </Col>
          </Row>
        </Card.Header>
        <Card.Body className={`bg-gray-900 px-40 ${$view.value.openStageCards?.includes(stage.id) ? 'd-block' : 'd-none'}`}>
          <DropZone stageKey={stage.value}>
            {pipelineItems?.map((pipelineItem) => (
              <Row key={pipelineItem.dealId} className="mb-16">
                <Col
                  className=""
                  xs={12}
                >
                  {
                    $view.value.view === 'grid' ? (
                      <FounderPipelineGridItem pipelineItem={pipelineItem} />
                    ) : (
                      <FounderPipelineListItem pipelineItem={pipelineItem} />
                    )
                  }
                </Col>
              </Row>
            ))}
            {/* Todo: Add back Add new button */}
            {/* <div className="d-flex justify-content-start align-items-center">
              <Button variant="transparent" size="sm" className="d-flex align-items-center text-gray-400">
                <FontAwesomeIcon icon={faPlus} className="me-8" size="sm" />
                Add New
              </Button>
            </div> */}
          </DropZone>
        </Card.Body>
      </Card>
    );
  });

export default FounderPipelineCards;
